import { Injectable } from '@angular/core';
import {
  TREQUEST_ALLOCATE_ORDER_MULTIPLE_FACTORIES,
  TREQUEST_ASSIGN_TNA_TO_ORDERS,
  TREQUEST_ORDER_ALLOCATION_CREATE_SPLIT_VIEW,
  TREQUEST_ORDER_SINGLE_FACTORY_ALLOCATION,
  TREQUEST_ORDER_VIEW_FACTORY_ALLOCATION,
  TREQUEST_DELETE_ORDERS,
  TFilterSelected,
  TOrderListViewType,
  TREQUEST_ORDER_DETAILS_DELETE_ALLOCATION,
  TREQUEST_ORDER_LIST_BY_BUCKET,
} from '@monorepo/types';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { TFilterGroupModel, TUserPaginationOption } from '@monorepo/ui';
import { select, Store } from '@ngrx/store';
import { TPlani8State } from '../reducers';
import * as orderListActions from './order-list.actions';
import * as orderListSelectors from './order-list.selectors';
import { catchError, finalize, lastValueFrom, map, of } from 'rxjs';

export const ORDER_LIST_ITEMS_PER_PAGE_LIST = [10, 100, 150, 200];
export const OPL_LIST_ITEMS_PER_PAGE_LIST = [10, 20];

@Injectable({ providedIn: 'root' })
export class OrderListFacade {
  constructor(
    private store: Store<TPlani8State>,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  fetchOrderList(payload: TREQUEST_ORDER_LIST_BY_BUCKET, path?: 'ASSIGN TNA TO ORDERS SCREEN') {
    this.store.dispatch(new orderListActions.GetOrderList(payload, path));
  }

  getOrderList() {
    return this.store.pipe(select(orderListSelectors.getOrderListInfo));
  }

  getUserPaginationOption() {
    return this.store.pipe(select(orderListSelectors.getUserPaginationOption));
  }

  setUserPaginationOption(option: TUserPaginationOption) {
    return this.store.dispatch(new orderListActions.SetUserPaginationOption(option));
  }

  fetchOrderUploadDetails() {
    this.store.dispatch(new orderListActions.GetOrderUploadDetails());
  }

  getOrderUploadDetails() {
    return this.store.pipe(select(orderListSelectors.getOrderUploadDetailsInfo));
  }

  assignTnaToOrders(payload: TREQUEST_ASSIGN_TNA_TO_ORDERS) {
    return this.store.dispatch(new orderListActions.AssignTnaToOrders(payload));
  }

  assignSingleFactoryToOrder(payload: TREQUEST_ORDER_SINGLE_FACTORY_ALLOCATION) {
    return this.store.dispatch(new orderListActions.AssignSingleFactoryToOrder(payload));
  }

  viewFactoryAllocation(payload: TREQUEST_ORDER_VIEW_FACTORY_ALLOCATION) {
    return this.store.dispatch(new orderListActions.ViewFactoryAllocation(payload));
  }

  fetchFactoryOrderSplit(payload: TREQUEST_ORDER_ALLOCATION_CREATE_SPLIT_VIEW) {
    return this.store.dispatch(new orderListActions.GetOrderToFactoriesSplit(payload));
  }

  allocateOrderToMultipleFactories(payload: TREQUEST_ALLOCATE_ORDER_MULTIPLE_FACTORIES) {
    return this.store.dispatch(new orderListActions.AllocateOrderToMultipleFactories(payload));
  }

  getFactoryOrderSplit() {
    return this.store.pipe(select(orderListSelectors.getFactoryOrderSplitInfo));
  }

  deleteOrders(deletePayload: TREQUEST_DELETE_ORDERS, fetchOrderListPayload: TREQUEST_ORDER_LIST_BY_BUCKET) {
    return this.store.dispatch(new orderListActions.DeleteOrders(deletePayload, fetchOrderListPayload));
  }

  fetchViewOrderFactoryAllocation(viewOrderFactoryAllocaitonPayload: TREQUEST_ORDER_VIEW_FACTORY_ALLOCATION) {
    return this.store.dispatch(new orderListActions.GetViewOrderFactoryAllocation(viewOrderFactoryAllocaitonPayload));
  }

  setOrderListingFilterGroupModel(filterModel: TFilterGroupModel<TFilterSelected>) {
    this.store.dispatch(new orderListActions.SetOrderListSelectedFilters(filterModel));
  }

  getOrderListingFilterGroupModel() {
    return this.store.pipe(select(orderListSelectors.getOrderListSelectedFilters));
  }

  setOrderListingViewType(viewType: TOrderListViewType) {
    this.store.dispatch(new orderListActions.SetOrderListBucketInfo(viewType));
  }

  getOrderListingViewType() {
    return this.store.pipe(select(orderListSelectors.getOrderListBucketInfo));
  }

  async deleteAllocation(payload: TREQUEST_ORDER_DETAILS_DELETE_ALLOCATION) {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.deleteAllocation(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }
}

export type TGetOrderListInfo = ReturnType<OrderListFacade['getOrderList']>;
export type TGetOrderUploadDetailsInfo = ReturnType<OrderListFacade['getOrderUploadDetails']>;
export type TGetFactoryOrderSplitInfo = ReturnType<OrderListFacade['getFactoryOrderSplit']>;
